import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type {
  InventoryBulkArchiveResponse,
  InventoryCategoryCreateResponse,
  InventoryCategoryUpdateResponse,
  InventoryFieldCreateResponse,
  InventoryFieldDeleteResponse,
  InventoryFieldType,
  InventoryFieldUpdateResponse,
  InventoryItemCreateResponse,
  InventoryItemPatchResponse,
  InventoryItemUpdateResponse,
  InventoryListResponse,
  InventoryRestoreResponse,
} from './contractsMirror.js';
import type { InventorySearch } from './searchParams.js';

/**
 * TanStack Query hook for the COARSE inventory endpoint (the strangler-fig + coarse-endpoint
 * proof, CARVE-07). One query key per `{category, archived}` — toggling either drives exactly one
 * refetch of `GET /api/v1/inventory?category=…&archived=…`. The SERVER computes the items,
 * per-category counts, and low-stock flags (22-08); the SPA only renders the returned
 * `InventoryListResponse` (no client re-aggregation, no new N+1 — threat T-22-tenant / CARVE-07).
 *
 * Every request goes through `apiFetch` (the only fetch path — `credentials:'include'` +
 * `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is derived server-side
 * from the auth context; the SPA NEVER sends a tenant id param (threat T-22-tenant).
 */

/** Build the stable query key for a filter (one cache entry per category + archived state). */
export function inventoryQueryKey(search: InventorySearch): readonly unknown[] {
  return ['inventory', search.category ?? null, search.archived ?? false] as const;
}

/** Build the coarse-endpoint request path with the active filters (no tenant param). */
export function inventoryRequestPath(search: InventorySearch): string {
  const params = new URLSearchParams();
  if (search.category) params.set('category', String(search.category));
  if (search.archived) params.set('archived', '1');
  const qs = params.toString();
  return qs ? `/api/v1/inventory?${qs}` : '/api/v1/inventory';
}

export function useInventoryQuery(search: InventorySearch) {
  return useQuery<InventoryListResponse>({
    queryKey: inventoryQueryKey(search),
    queryFn: () => apiFetchJson<InventoryListResponse>(inventoryRequestPath(search)),
  });
}

/**
 * The inline quantity-adjust mutation (UI-SPEC "inline edit"). A qty change PATCHes
 * `/api/v1/inventory/items/:id` and, on success, invalidates the inventory list so the row
 * re-syncs to the authoritative server quantity. The request carries NO tenantId (the server
 * resolves the tenant from the principal — threat T-22-tenant) and rides `apiFetch`'s
 * double-submit CSRF on the non-safe method. The server NEVER touches the custom_fields JSON on
 * a qty PATCH (T-22-contract).
 */
export function useInventoryQuantityMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    InventoryItemPatchResponse,
    Error,
    { itemId: number; quantity: number; reason?: string }
  >({
    mutationFn: ({ itemId, quantity, reason }) =>
      apiFetchJson<InventoryItemPatchResponse>(`/api/v1/inventory/items/${itemId}`, {
        method: 'PATCH',
        // NO tenantId — the server resolves the tenant from the principal (SEC-11). An optional
        // trimmed `reason` is logged to the activity feed by the server; omitted when blank.
        body: JSON.stringify({ quantity, ...(reason?.trim() ? { reason: reason.trim() } : {}) }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export interface EditInventoryItemInput {
  itemId: number;
  name: string;
  quantity: number;
  quantityUnit?: string;
  notes?: string;
  customFields: Record<string, string | number>;
}

/**
 * The full item-edit mutation (22-25). PATCHes `/api/v1/inventory/items/:id` WITH a `name`, which
 * the server treats as a full update (name / quantity / unit / notes / custom_fields), keeping the
 * item in its category. NO tenantId — the server resolves the tenant from the principal (SEC-11).
 */
export function useEditInventoryItemMutation() {
  const queryClient = useQueryClient();

  return useMutation<InventoryItemUpdateResponse, Error, EditInventoryItemInput>({
    mutationFn: ({ itemId, name, quantity, quantityUnit, notes, customFields }) =>
      apiFetchJson<InventoryItemUpdateResponse>(`/api/v1/inventory/items/${itemId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name,
          quantity,
          ...(quantityUnit ? { quantityUnit } : {}),
          ...(notes ? { notes } : {}),
          customFields,
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

/**
 * The item-restore mutation (22-25). Un-archives the selected item ids via
 * `POST /api/v1/inventory/restore`; the server re-checks the `inventory.archive` permission and
 * scopes every UPDATE to the principal tenant (SEC-11). Invalidates the inventory list on success.
 */
export function useRestoreInventoryMutation() {
  const queryClient = useQueryClient();

  return useMutation<InventoryRestoreResponse, Error, { ids: number[] }>({
    mutationFn: ({ ids }) =>
      apiFetchJson<InventoryRestoreResponse>('/api/v1/inventory/restore', {
        method: 'POST',
        body: JSON.stringify({ ids }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export interface EditInventoryCategoryInput {
  categoryId: number;
  name: string;
  description?: string;
}

/** Rename/describe an existing category (22-25) and refresh inventory caches. */
export function useEditInventoryCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation<InventoryCategoryUpdateResponse, Error, EditInventoryCategoryInput>({
    mutationFn: ({ categoryId, name, description }) =>
      apiFetchJson<InventoryCategoryUpdateResponse>(`/api/v1/inventory/categories/${categoryId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name, ...(description ? { description } : {}) }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export interface EditInventoryFieldInput {
  fieldId: number;
  label: string;
  fieldType: InventoryFieldType;
  unit?: string;
  required: boolean;
  options?: string[];
}

/**
 * Edit a custom-field definition (22-25). The server holds `field_key` STABLE on edit so stored
 * item custom_fields values are never orphaned. Refreshes inventory caches on success.
 */
export function useEditInventoryFieldMutation() {
  const queryClient = useQueryClient();

  return useMutation<InventoryFieldUpdateResponse, Error, EditInventoryFieldInput>({
    mutationFn: ({ fieldId, label, fieldType, unit, required, options }) =>
      apiFetchJson<InventoryFieldUpdateResponse>(`/api/v1/inventory/fields/${fieldId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          label,
          field_type: fieldType,
          required,
          ...(unit ? { unit } : {}),
          ...(fieldType === 'select' && options && options.length > 0 ? { options } : {}),
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

/** Delete a custom-field definition (22-25) and refresh inventory caches. */
export function useDeleteInventoryFieldMutation() {
  const queryClient = useQueryClient();

  return useMutation<InventoryFieldDeleteResponse, Error, { fieldId: number }>({
    mutationFn: ({ fieldId }) =>
      apiFetchJson<InventoryFieldDeleteResponse>(`/api/v1/inventory/fields/${fieldId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

/**
 * The bulk-archive mutation (UI-SPEC "bulk actions"). Archives the selected item ids via
 * `POST /api/v1/inventory/bulk-archive`; the server re-checks the `inventory.archive` permission
 * (T-22-archive) and scopes every UPDATE to the principal tenant (SEC-11). On success the
 * inventory list is invalidated so the archived rows drop out of the active view.
 */
export function useInventoryBulkArchiveMutation() {
  const queryClient = useQueryClient();

  return useMutation<InventoryBulkArchiveResponse, Error, { ids: number[] }>({
    mutationFn: ({ ids }) =>
      apiFetchJson<InventoryBulkArchiveResponse>('/api/v1/inventory/bulk-archive', {
        method: 'POST',
        // NO tenantId — the server resolves the tenant from the principal (SEC-11).
        body: JSON.stringify({ ids }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export interface CreateInventoryCategoryInput {
  name: string;
  description?: string;
}

/** Create a category and refresh inventory caches so counts/categories stay in sync. */
export function useCreateInventoryCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation<InventoryCategoryCreateResponse, Error, CreateInventoryCategoryInput>({
    mutationFn: ({ name, description }) =>
      apiFetchJson<InventoryCategoryCreateResponse>('/api/v1/inventory/categories', {
        method: 'POST',
        body: JSON.stringify({
          name,
          ...(description ? { description } : {}),
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export interface CreateInventoryFieldInput {
  categoryId: number;
  label: string;
  fieldType: InventoryFieldType;
  unit?: string;
  required: boolean;
  options?: string[];
}

/** Create a field definition under one category and refresh inventory caches. */
export function useCreateInventoryFieldMutation() {
  const queryClient = useQueryClient();

  return useMutation<InventoryFieldCreateResponse, Error, CreateInventoryFieldInput>({
    mutationFn: ({ categoryId, label, fieldType, unit, required, options }) =>
      apiFetchJson<InventoryFieldCreateResponse>(
        `/api/v1/inventory/categories/${categoryId}/fields`,
        {
          method: 'POST',
          body: JSON.stringify({
            label,
            field_type: fieldType,
            required,
            ...(unit ? { unit } : {}),
            ...(fieldType === 'select' && options && options.length > 0 ? { options } : {}),
          }),
          headers: { 'Content-Type': 'application/json' },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export interface CreateInventoryItemInput {
  categoryId: number;
  name: string;
  quantity: number;
  quantityUnit?: string;
  notes?: string;
  customFields: Record<string, string | number>;
}

/** Create an inventory item and refresh all inventory list/detail caches. */
export function useCreateInventoryItemMutation() {
  const queryClient = useQueryClient();

  return useMutation<InventoryItemCreateResponse, Error, CreateInventoryItemInput>({
    mutationFn: ({ categoryId, name, quantity, quantityUnit, notes, customFields }) =>
      apiFetchJson<InventoryItemCreateResponse>('/api/v1/inventory/items', {
        method: 'POST',
        body: JSON.stringify({
          categoryId,
          name,
          quantity,
          ...(quantityUnit ? { quantityUnit } : {}),
          ...(notes ? { notes } : {}),
          customFields,
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}
